import { LocalizationUtils } from "@shared/utils";

const lowerCaseFormat = "lowercase";

export const BackendErrorDynamicMessages: {
  [key: string]: (args: any) => string;
} = {
  ALREADY_EXISTS: (args) => $localize`:@@backendErrors.alreadyExists:
      ${args.name}:name: already exists`,

  ATTACHMENT_ALREADY_EXISTS: (args) => {
    const resourceType = LocalizationUtils.getEntityType(
      args.resource_type,
      false,
      lowerCaseFormat,
    );
    const otherResourceType = LocalizationUtils.getEntityType(
      args.other_resource_type,
      false,
      lowerCaseFormat,
    );

    return $localize`:@@backendErrors.attachmentAlreadyExists:
  Attachment link from ${resourceType}:resource_type: ${args.entity_name}:entity_name: (id: ${args.id}:id:)
  to ${otherResourceType}:other_resource_type: ${args.other_entity_name}:other_entity_name: (id: ${args.other_id}:other_id:)
  already exists`;
  },

  CUSTOM_ERROR: (_args?) => $localize`:@@backendErrors.custom-error:
      Something went wrong. If the issue persists, please contact support.`,

  DOES_NOT_EXIST: (args) => $localize`:@@backendErrors.doesNotExist:
      ${args.name}:name: does not exist`,

  ENTITY_IS_STILL_REFERENCED: (args) => {
    const resourceType = LocalizationUtils.getEntityType(
      args.resource_type,
      false,
      lowerCaseFormat,
    );
    const otherResourceType = LocalizationUtils.getEntityType(
      args.other_resource_type,
      false,
      lowerCaseFormat,
    );

    return $localize`:@@backendErrors.entityIsStillReferenced:
  The ${resourceType}:resource_type: is still referenced by ${otherResourceType}:other_resource_type:`;
  },

  INVALID_ARGUMENT: (args) => $localize`:@@backendErrors.invalidArgument:
      The argument value ${args.value}:value: is invalid`,

  INVALID_ELEMENT_COUNT: (args) => $localize`:@@backendErrors.invalidElementCount:
      Element count cannot be more than ${args.max}:max: `,

  INVALID_LIST_OPTIONS_SIZE: (args) => $localize`:@@backendErrors.invalidListOptionsSize:
      Custom field definition type 'list' must have minimum ${args.min}:min: and maximum ${args.max}:max: options to select from`,

  INVALID_MAX_LENGTH: (args) => $localize`:@@backendErrors.invalidMaxLength:
      Length must not be larger than ${args.max}:max:`,

  INVALID_MAX_VALUE: (args) => $localize`:@@backendErrors.invalidMaxValue:
      Value must be smaller than ${args.max}:max:`,

  INVALID_MIN_LENGTH: (args) => $localize`:@@backendErrors.invalidMinLength:
      Length must not be smaller than ${args.min}:min:`,

  INVALID_MIN_VALUE: (args) => $localize`:@@backendErrors.invalidMinValue:
      Value must be larger than ${args.min}:min:`,

  INVALID_PROPERTY_BINDING: (_args?) => $localize`:@@backendErrors.invalid-property-binding:
      There is an issue with one of the provided values. Please check your input and try again.`,

  INVALID_PROPERTY_VALUE: (args) => $localize`:@@backendErrors.invalid-property-value:
     The value '${args.value}:value:' you entered is not valid. Please check and try again.`,

  INVALID_RANGE_LENGTH: (args) => $localize`:@@backendErrors.invalidRangeLength:
      Length must be between ${args.min}:min: and ${args.max}:max:`,

  INVALID_RESOURCE_OWNER: (args) => $localize`:@@backendErrors.invalidResourceOwner:
      The resource owner '${args.id}:id:' is not allowed`,

  INVALID_RESOURCE_TYPE: (args) => {
    const resourceType = LocalizationUtils.getEntityType(
      args.resource_type,
      false,
      lowerCaseFormat,
    );

    return $localize`:@@backendErrors.invalidResourceType:
  The resource type '${resourceType}:resource_type:' is not allowed`;
  },

  INVALID_VALUE_RANGE: (args) => $localize`:@@backendErrors.invalidValueRange:
      Value must be between ${args.min}:min: and ${args.max}:max:`,

  MISSING_PROPERTY: (args) => $localize`:@@backendErrors.missingProperty:
      ${args.property}:property: must be specified`,

  NOT_ALLOWED_TO_ADD_ARCHIVED_ENTITY: (args) => {
    const resourceType = LocalizationUtils.getEntityType(args.resource_type);

    return $localize`:@@backendErrors.notAllowedToAddArchivedEntity:
  ${resourceType}:resource_type: ${args.entity_name}:entity_name: (id: ${args.id}:id:) cannot be added because it is archived`;
  },

  NOT_ALLOWED_TO_DELETE_REFERENCED_ENTITY: (args) => {
    const resourceType = LocalizationUtils.getEntityType(args.resource_type);
    const otherResourceType = LocalizationUtils.getEntityType(
      args.other_resource_type,
      false,
      lowerCaseFormat,
    );

    return $localize`:@@backendErrors.notAllowedToDeleteReferencedEntity:
  ${resourceType}:resource_type: ${args.entity_name}:entity_name: (id: ${args.id}:id:)
  is still referenced by ${otherResourceType}:other_resource_type: ${args.other_entity_name}:other_entity_name: (id: ${args.other_id}:other_id:)
  and cannot be deleted`;
  },

  NOT_ALLOWED_TO_EDIT_ARCHIVED_RECORDS: (
    args,
  ) => $localize`:@@backendErrors.notAllowedToEditArchivedRecords:
  ${args.resource_type}:resource_type: ${args.entity_name}:entity_name: (id: ${args.id}:id:) cannot be edited because it is archived`,

  NOT_ALLOWED_TO_EDIT_ON_STATE_TRANSITION: (args) => {
    const resourceType = LocalizationUtils.getEntityType(
      args.resource_type,
      false,
      lowerCaseFormat,
    );

    return $localize`:@@backendErrors.notAllowedToEditOnStateTransition:
  ${resourceType}:resource_type: ${args.entity_name}:entity_name: (id: ${args.id}:id:) cannot be edited on record state transition`;
  },

  NOT_ALLOWED_TO_DELETE_ATTACHED_RECORD: (args) => {
    const resourceType = LocalizationUtils.getEntityType(args.resource_type);
    const otherResourceType = LocalizationUtils.getEntityType(
      args.other_resource_type,
      false,
      lowerCaseFormat,
    );

    return $localize`:@@backendErrors.notAllowedToDeleteAttachedRecord:
  ${resourceType}:resource_type: ${args.entity_name}:entity_name: (id: ${args.id}:id:)
  is still used in an attachment with ${otherResourceType}:other_resource_type: ${args.other_entity_name}:other_entity_name: (id: ${args.other_id}:other_id:)
  and cannot be deleted`;
  },

  NOT_ALLOWED_TO_DELETE_SHARED_RECORD: (
    args,
  ) => $localize`:@@backendErrors.notAllowedToDeleteSharedRecord:
  ${args.resource_type}:resource_type: ${args.entity_name}:entity_name: (id: ${args.id}:id:) cannot be deleted, as it is shared.`,

  RESOURCE_ALREADY_EXISTS: (args) => {
    const resourceType = LocalizationUtils.getEntityType(
      args.resource_type,
      false,
      "capitaliseFirstLetter",
    );

    return $localize`:@@backendErrors.resourceAlreadyExists:
    ${resourceType}:resource_type: ${args.entity_name}:entity_name: already exists`;
  },

  RESOURCE_NOT_FOUND: (args) => $localize`:@@backendErrors.resourceNotFound:
      The resource ${args.uri}:uri: could not be found`,

  DUPLICATE_STANDARD_TYPE_FULL_NAME: (
    args,
  ) => $localize`:@@backendErrors.duplicateStandardTypeFullName:
      Certification standard type full name ${args.standard_name}:standard_name: already exists for this standard`,

  DUPLICATE_STANDARD_TYPE_SHORT_NAME: (
    args,
  ) => $localize`:@@backendErrors.duplicateStandardTypeShortName:
      Certification standard type short name ${args.standard_name}:standard_name: already exists for this standard`,

  INVALID_STANDARD_TYPE: (args) => $localize`:@@backendErrors.invalidStandardType:
      Certificate standard type ${args.standard_type_name}:standard_type_name: is not part of certificate standard ${args.standard_name}:standard_name:`,

  INVALID_PRODUCT_MATERIAL: (args) => $localize`:@@backendErrors.invalidProductMaterial:
      Material ${args.entity_name}:entity_name: (id: ${args.id}:id:) is not part of the allowed materials of product ${args.other_entity_name}:other_entity_name: (id: ${args.other_id}:other_id:)`,

  INVALID_SUPPLY_CHAIN_CERTIFICATE: (
    args,
  ) => $localize`:@@backendErrors.invalidSupplyChainCertificate:
      Certificate ${args.entity_name}:entity_name: is not part of location ${args.other_entity_name}:other_entity_name:`,

  INVALID_SUPPLY_CHAIN_DOCUMENT: (args) => $localize`:@@backendErrors.invalidSupplyChainDocument:
      Document ${args.entity_name}:entity_name: is not part of location ${args.other_entity_name}:other_entity_name:`,

  INVALID_SUPPLY_CHAIN_LOCATION: (args) => $localize`:@@backendErrors.invalidSupplyChainLocation:
      Location ${args.entity_name}:entity_name: is not part of the supply chain but a link exists`,

  NOT_ALLOWED_TO_DELETE_SUPPLY_CHAIN_ATTACHMENT: (args) => {
    const resourceType = LocalizationUtils.getEntityType(
      args.resource_type,
      false,
      lowerCaseFormat,
    );

    return $localize`:@@backendErrors.notAllowedToDeleteSupplyChainAttachment:
  The attachment link for ${resourceType}:resource_type: ${args.entity_name}:entity_name: (id: ${args.id}:id:)
  cannot be deleted it is required for supply chain ${args.other_entity_name}:other_entity_name: (id: ${args.other_id}:other_id:)`;
  },

  DOCUMENT_CONTENT_TOO_BIG: (args) => $localize`:@@backendErrors.documentContentTooBig:
      Content length exceeds the max allowed length of ${args.max}:max: bytes`,

  NOT_ALLOWED_TO_ATTACH_DOCUMENT_NO_CONTENT: (
    args,
  ) => $localize`:@@backendErrors.notAllowedToAttachDocumentNoContent:
      Document ${args.entity_name}:entity_name: (id: ${args.id}:id:) cannot be attached because it has no content.`,

  DUPLICATE_PROCESS_INPUT: (args) => $localize`:@@backendErrors.duplicateProcessInput:
      Duplicated input (Item: ${args.item_id}:item_id:)`,

  DUPLICATE_PROCESS_OUTPUT: (args) => $localize`:@@backendErrors.duplicateProcessOutput:
      Duplicated output (Item: ${args.id}:id:)`,

  INVALID_PROCESS_INPUT: (args) => $localize`:@@backendErrors.invalidProcessInput:
      Input item (id: ${args.item_id}:item_id:) is already present in an output of the process`,

  INVALID_PROCESS_STATE: (args) => {
    const processState = LocalizationUtils.getProcessStateLabel(
      args.process_state,
    ).toLocaleLowerCase();

    return $localize`:@@backendErrors.invalid-process-state:
      Process is not in ${processState}:process_state: state`;
  },

  PROCESS_OUTPUT_ITEM_ALREADY_EXISTS: (
    args,
  ) => $localize`:@@backendErrors.processOutputItemAlreadyExists:
      Output item (id: ${args.item_id}:item_id:) is already present in an input of the process`,

  PROCESS_OUTPUT_ITEM_ALREADY_PROVIDED: (
    args,
  ) => $localize`:@@backendErrors.processOutputItemAlreadyProvided:
      Output (Item: ${args.item_id}:item_id:) is already part of the process`,

  REMOVE_RESOURCE_TYPE_FORBIDDEN: (args) => {
    const resourceType = LocalizationUtils.getEntityType(
      args.resource_type,
      false,
      lowerCaseFormat,
    );

    return $localize`:@@backendErrors.removeResourceTypeForbidden:
  Cannot remove resource type ${resourceType}:resource_type: because it is used in records`;
  },

  INVALID_CUSTOM_FIELD_TYPE: (args) => {
    const resourceType = LocalizationUtils.getEntityType(
      args.resource_type,
      false,
      lowerCaseFormat,
    );

    return $localize`:@@backendErrors.invalidCustomFieldType:
  The type of the custom field value has to be ${resourceType}:resource_type:`;
  },

  INVALID_GEO_JSON: (_args?) => $localize`:@@backendErrors.invalid-geo-json:
      The provided GeoJSON is not valid.`,

  LOCATION_GEO_JSON_FEATURE_LIMIT_EXCEEDED: (
    args,
  ) => $localize`:@@backendErrors.locationGeoJsonFeatureLimitExceeded:
      The GeoJSON feature limit has been exceeded (only ${args.max}:max: features are allowed)`,

  UNSUPPORTED_GEO_JSON_FEATURE_TYPE: (
    args,
  ) => $localize`:@@backendErrors.unsupportedGeoJsonFeatureType:
      The provided GeoJSON feature type is not supported: ${args.name}:name:`,

  MODULE_NOT_FOUND: (args) => $localize`:@@backendErrors.moduleNotFound:
      Extension module for ${args.provider_id}:provider_id: not found`,

  NO_MODULE_ACCESS: (args) => $localize`:@@backendErrors.noModuleAccess:
      User does not have access to the extension module for ${args.provider_id}:provider_id:`,

  NO_MODULES_AVAILABLE: (args) => $localize`:@@backendErrors.noModulesAvailable:
      No available modules for provider ${args.provider_id}:provider_id:`,

  EXPRESSION_PATH_INVALID: (_args?) => $localize`:@@backendErrors.expression-path-invalid:
      There seems to be an issue with the ruleset. Please ensure all required fields are properly defined.`,

  EXPRESSION_VALUE_INVALID: (args) => $localize`:@@backendErrors.expression-value-invalid:
      The provided value [${args.value}:value:] is not valid for this ruleset.`,

  SHARE_ALREADY_EXISTS: (args) => {
    const resourceType = LocalizationUtils.getEntityType(
      args.resource_type,
      false,
      lowerCaseFormat,
    );

    return $localize`:@@backendErrors.shareAlreadyExists:
  Share for ${resourceType}:resource_type: ${args.entity_name}:entity_name: (id: ${args.id}:id:) already exists`;
  },

  SHARED_ENTITY_ALREADY_EXISTS: (args) => $localize`:@@backendErrors.shared-entity-alreadyExists:
      Entity [${args.id}:id:] already exists`,

  SHARING_NOT_SUPPORTED: (args) => {
    const resourceType = LocalizationUtils.getEntityType(
      args.resource_type,
      false,
      lowerCaseFormat,
    );

    return $localize`:@@backendErrors.sharingNotSupported:
  Sharing ${resourceType}:resource_type: is not supported at the moment`;
  },

  DUPLICATE_RISK_REPORT: (args) => {
    const resourceType = LocalizationUtils.getEntityType(
      args.resource_type,
      false,
      lowerCaseFormat,
    );

    return $localize`:@@backendErrors.duplicateRiskReport:
  Duplicate risk report for ${resourceType}:resource_type: ${args.entity_name}:entity_name: (id: ${args.id}:id:)`;
  },

  INVALID_RISK_REPORT_RESOURCE_TYPE: (args) => {
    const resourceType = LocalizationUtils.getEntityType(
      args.resource_type,
      false,
      lowerCaseFormat,
    );

    return $localize`:@@backendErrors.invalidRiskReportResourceType:
  Invalid resource type for risk report: ${resourceType}:resource_type:`;
  },

  NOT_ALLOWED_TO_DELETE_MAPPED_RECORD: (args) => {
    const resourceType = LocalizationUtils.getEntityType(args.resource_type);
    const otherResourceType = LocalizationUtils.getEntityType(
      args.other_resource_type,
      false,
      lowerCaseFormat,
    );

    return $localize`:@@backendErrors.notAllowedToDeleteMappedRecord:
      ${resourceType}:resource_type: ${args.entity_name}:entity_name: (id: ${args.id}:id:)
      cannot be deleted because it is mapped to ${otherResourceType}:other_resource_type: ${args.other_entity_name}:other_entity_name: (id: ${args.other_id}:other_id:)`;
  },
};
